import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  output,
  inject,
  input,
} from '@angular/core';
import type { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';

import moment from 'moment-timezone';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import type { AdminReportConfiguration } from '@core/account-types';
import { AdminReportInterval } from '@core/account-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { ScheduleTimePickerComponent } from '@core/interview-find-time-ui';
import type { SelectOption } from '@core/user-interface-legacy';
import { HeadingComponent, SelectComponent } from '@core/user-interface-legacy';

@Component({
  selector: 'rt-activity-subscriptions-report',
  templateUrl: './activity-subscriptions-report.component.html',
  styleUrls: ['./activity-subscriptions-report.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    HeadingComponent,
    TranslatePipe,
    SelectComponent,
    ScheduleTimePickerComponent,
  ],
})
export class ActivitySubscriptionsReportComponent {
  private readonly fb = inject(UntypedFormBuilder);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly i18nService = inject(I18nService);

  public readonly adminReportConfiguration = input<AdminReportConfiguration>(undefined!);
  public reportConfigurationChanged = output<AdminReportConfiguration>();

  public AdminReportInterval: typeof AdminReportInterval = AdminReportInterval;
  public weekDaysOptions: Array<SelectOption<number>>;
  public intervalOptions: Array<SelectOption<string>>;
  public form: UntypedFormGroup = this.fb.group({
    time: null,
    interval: null,
    day: null,
  });

  private weekDaysLabels: string[] = [...Array(7)].map((_, index) =>
    moment()
      .day(index + 1)
      .format('dddd'),
  );
  private ngOnDestroy$ = new Subject<void>();

  public ngOnChanges(): void {
    this.form.patchValue(this.adminReportConfiguration(), { emitEvent: false });
  }

  public ngOnInit(): void {
    this.weekDaysOptions = this.weekDaysLabels.map((label, index) => ({
      label,
      value: index + 1,
    }));

    this.intervalOptions = this.getIntervalOptions();
    this.form.valueChanges.pipe(takeUntil(this.ngOnDestroy$.asObservable())).subscribe(value => {
      this.reportConfigurationChanged.emit(value);
      this.cdRef.detectChanges();
    });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public get intervalControl(): AbstractControl {
    return this.form.get('interval')!;
  }

  public onTimeChange(time: string): void {
    this.form.patchValue({ time: time });
  }

  private getIntervalOptions(): Array<SelectOption<string>> {
    return [
      {
        label: this.i18nService.translate('app.account.notification_options.daily'),
        value: AdminReportInterval.Day,
      },
      {
        label: this.i18nService.translate('app.account.notification_options.weekly'),
        value: AdminReportInterval.Week,
      },
      {
        label: this.i18nService.translate('app.account.notification_options.never'),
        value: AdminReportInterval.Never,
      },
    ];
  }
}
